import { HintTooltip } from '@/components/HintTooltip';
import { Badge } from '@/components/ui/badge';
import type { BrandRequestSuggestion } from '@rock/types';
import type { FC } from 'react';

const KIND_LABELS: Record<BrandRequestSuggestion['kind'], string> = {
    alias: 'alias of',
    line: 'line of',
    shared: 'shared nameplate',
};

/** The catalog hints for a request, as chips: "line of Hunter Fan". Hints only — a human decides. */
export const SuggestionChips: FC<{
    suggestions: BrandRequestSuggestion[];
    /** Show the strongest few, for a table cell. */
    compact?: boolean;
    onPick?: (suggestion: BrandRequestSuggestion) => void;
}> = ({ suggestions, compact, onPick }) => {
    const shown = compact ? suggestions.slice(0, 3) : suggestions;
    if (shown.length === 0) return <span className="text-muted-foreground">—</span>;
    return (
        <span className="flex flex-wrap items-center gap-1">
            {shown.map((suggestion) => {
                const label = `${KIND_LABELS[suggestion.kind]}${suggestion.brandName ? ` ${suggestion.brandName}` : ''}`;
                const key = `${suggestion.kind}:${suggestion.brandId ?? ''}`;
                const hint = (
                    <>
                        <span>{suggestion.detail}</span>
                        <span className="opacity-70">A hint from the catalog, not a decision.</span>
                        {onPick && <span className="opacity-70">Click to start resolving it this way.</span>}
                    </>
                );
                return (
                    <HintTooltip key={key} hint={hint} className={onPick ? 'cursor-pointer' : undefined}>
                        {onPick ? (
                            <button type="button" onClick={() => onPick(suggestion)}>
                                <Badge variant="outline" className="cursor-pointer hover:bg-muted">
                                    {label}
                                </Badge>
                            </button>
                        ) : (
                            <Badge variant="outline">{label}</Badge>
                        )}
                    </HintTooltip>
                );
            })}
            {compact && suggestions.length > shown.length && (
                <span className="text-xs text-muted-foreground">+{suggestions.length - shown.length}</span>
            )}
        </span>
    );
};
